import { useCreateConnector } from "@/features/connectors/hooks/use-connectors";
import { useTranslation } from "@/features/utils/i18n";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import type { FormChannel } from "./channels";
import { ConnectorForm, type ConnectorFormData } from "./connector-form";
import {
  emailAccountPayload,
  sipRegistrationPayload,
} from "./connector-form/types";

/**
 * The generic connector form, for the channels the picker sends here: a phone
 * line, an e-mail box, a web chat. (A Meta account is connected, not typed in —
 * see `connect-meta/`.)
 */
export const ConnectorCreateForm = ({
  channel,
  onCreated,
}: {
  channel: FormChannel;
  onCreated: () => void;
}) => {
  const { t } = useTranslation("connectors");
  const { company: clientId } = useParams<{ company: string }>();

  const createConnector = useCreateConnector();

  const handleCreate = async (formData: ConnectorFormData) => {
    if (!clientId) {
      toast.error(t("connectors.messages.noClientId"));
      return;
    }

    const phoneNumbersArray = formData.phoneNumbers
      .split(",")
      .map((num) => num.trim())
      .filter(Boolean);

    const whatsappNumbersArray = formData.whatsappNumbers
      .split(",")
      .map((num) => num.trim())
      .filter(Boolean);

    try {
      await createConnector.mutateAsync({
        clientId,
        data: {
          type: formData.type,
          status: formData.status,
          capabilities: formData.capabilities,
          agent_id: formData.agentId || undefined,
          configuration: {
            account_id: formData.accountId,
            auth_token: formData.authToken,
            twilio_region: formData.twilioRegion,
            phone_numbers: phoneNumbersArray,
            whatsapp_numbers: whatsappNumbersArray,
            routing_rules: formData.routingRules,
            geo_filter: formData.geoFilter,
            outbound_filter: formData.outboundFilter,
            fallback_destinations: formData.fallbackDestinations,
            agent_off_schedule_id: formData.agentOffScheduleId,
            ring_before_agent_seconds: formData.ringBeforeAgentSeconds,
            // Cleared on save: the ordered list above is the only source of
            // truth once a connector has been edited.
            fallback_destination: undefined,
            fallback_member_id: undefined,
            fallback_number: undefined,
            allowed_origins: formData.allowedOrigins
              .split(/[,\n]/)
              .map((o) => o.trim())
              .filter(Boolean),
            caller_numbers: formData.callerNumbers
              .split(",")
              .map((num) => num.trim())
              .filter(Boolean),
            demo_number: formData.demoNumber,
            code_routing: formData.codeRouting,
            default_connector: formData.defaultConnector,
            web_call_enabled: formData.webCallEnabled,
            public_callback_enabled: formData.publicCallbackEnabled,
            sip_registration: sipRegistrationPayload(formData),
            email_account: emailAccountPayload(formData),
          },
        },
      });

      toast.success(t("connectors.messages.created"));
      onCreated();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("connectors.messages.createError"),
      );
    }
  };

  return (
    <ConnectorForm
      mode="create"
      channel={channel}
      onSubmit={handleCreate}
      isSubmitting={createConnector.isPending}
    />
  );
};
